import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import Avatar from "../../common/AvtarImage";
import appRoutes from "../../../routes/app.routes";
import networkServices from "../../../services/myNetwork.services";
import { openUrlInNewTab } from "../../../utils/helper";

const VISIBLE_AVATARS = 6;

interface ConnectionPerson {
  id?: number | string;
  slug?: string;
  first_name?: string;
  last_name?: string;
  profile_image?: string;
}

const ProfileConnections: React.FC = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<ConnectionPerson[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadConnections = async () => {
      setLoading(true);
      try {
        const response = await networkServices.searchAll({
          page: 1,
          pageSize: VISIBLE_AVATARS,
        });
        if (cancelled) return;

        const rows = _.get(response, "data.data", []) || [];
        const total =
          Number(_.get(response, "data.pagination.totalCount", rows.length)) ||
          rows.length;
        setConnections(rows);
        setTotalCount(total);
      } catch (error) {
        if (!cancelled) {
          setConnections([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConnections();
    return () => {
      cancelled = true;
    };
  }, []);

  const overflowCount = Math.max(totalCount - connections.length, 0);

  const openConnectionsPage = () => {
    navigate(appRoutes.candidateConnections.path);
  };

  const openConnectionProfile = (person: ConnectionPerson) => {
    if (!person?.slug) return;
    openUrlInNewTab(
      appRoutes.candidatePublicProfile.generateFullPath(person.slug)
    );
  };

  return (
    <div className="profile-connections-card">
      <div className="profile-connections-card__head">
        <b>Connections</b>
        {totalCount > 0 && (
          <button
            type="button"
            className="profile-connections-card__view-all"
            onClick={openConnectionsPage}
          >
            View all
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="profile-connections-card__avatars" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, index) => (
            <span
              key={index}
              className="profile-connections-card__skeleton"
            />
          ))}
        </div>
      ) : connections.length === 0 ? (
        <div className="profile-connections-card__empty">
          <p>You don’t have any connections yet. Grow your network to get noticed.</p>
          <button type="button" onClick={openConnectionsPage}>
            Find connections
          </button>
        </div>
      ) : (
        <div className="profile-connections-card__avatars">
          {connections.slice(0, VISIBLE_AVATARS).map((person) => {
            const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
            return (
              <button
                type="button"
                key={String(person.id || person.slug)}
                className="profile-connections-card__avatar"
                title={name || "View profile"}
                onClick={() => openConnectionProfile(person)}
              >
                <Avatar src={person.profile_image || ""} size="xsm" />
              </button>
            );
          })}
          {overflowCount > 0 && (
            <button
              type="button"
              className="profile-connections-card__more"
              onClick={openConnectionsPage}
              aria-label={`View ${overflowCount} more connections`}
            >
              +{overflowCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileConnections;
